import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '@/lib/auth/hooks/auth-provider';

export const WEB_SEARCH_DEFAULT_KEY = 'webSearchDefault';
const CHANGE_EVENT = 'web-search-default-changed';
const SAVE_FAILED_MESSAGE = 'Could not save web search default.';

export function webSearchDefaultStorageKey(accountId: string): string {
  return `${WEB_SEARCH_DEFAULT_KEY}:${accountId.trim().toLowerCase()}`;
}

export function readWebSearchDefault(accountId?: string | null): boolean {
  const id = accountId?.trim();
  if (!id) return false;
  try {
    return localStorage.getItem(webSearchDefaultStorageKey(id)) === 'true';
  } catch {
    return false;
  }
}

export const useWebSearchDefault = () => {
  const { user } = useAuth();
  const accountId = user?.email ?? null;
  const [enabled, setEnabled] = useState(() => readWebSearchDefault(accountId));

  useEffect(() => {
    const sync = () => setEnabled(readWebSearchDefault(accountId));
    sync();
    window.addEventListener('storage', sync);
    window.addEventListener(CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(CHANGE_EVENT, sync);
    };
  }, [accountId]);

  const save = useCallback((nextEnabled: boolean) => {
    const id = accountId?.trim();
    if (!id) {
      toast.error(SAVE_FAILED_MESSAGE);
      return;
    }
    try {
      localStorage.setItem(webSearchDefaultStorageKey(id), String(nextEnabled));
    } catch {
      toast.error(SAVE_FAILED_MESSAGE);
      return;
    }
    setEnabled(nextEnabled);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, [accountId]);

  return { enabled, save };
};

/**
 * Composer Web toggle. Starts at the account default and stays there until the
 * user flips it or a loaded chat restores its last value. Call
 * {@link resetWebSearchToDefault} when starting a new chat.
 */
export const useWebSearchToggleState = () => {
  const { enabled: webSearchDefault } = useWebSearchDefault();
  const [webSearchEnabled, setWebSearchEnabledState] = useState(webSearchDefault);
  const webSearchDefaultRef = useRef(webSearchDefault);
  webSearchDefaultRef.current = webSearchDefault;
  const userOverrideRef = useRef(false);

  useEffect(() => {
    if (userOverrideRef.current) return;
    setWebSearchEnabledState(webSearchDefault);
  }, [webSearchDefault]);

  const setWebSearchEnabled = useCallback((nextEnabled: boolean) => {
    userOverrideRef.current = true;
    setWebSearchEnabledState(nextEnabled);
  }, []);

  const resetWebSearchToDefault = useCallback(() => {
    userOverrideRef.current = false;
    setWebSearchEnabledState(webSearchDefaultRef.current);
  }, []);

  return {
    webSearchEnabled,
    setWebSearchEnabled,
    resetWebSearchToDefault,
  };
};
